import React, { useState, useEffect, useCallback } from 'react';
import {
    Table, Button, Modal, Form, Input, Select,
    message, Spin, Tag, Tooltip
} from 'antd';
import { PlusOutlined, UserOutlined, UserAddOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { motion, AnimatePresence } from 'framer-motion';
import { adminService } from '../../services/adminService';
const AgentsPage = () => {
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [form] = Form.useForm();

    const fetchAgents = useCallback(async () => {
        setLoading(true);
        try {
            const data = await adminService.getAllAgents();
            setAgents(data);
        } catch (err) {
            message.error('Temsilciler yüklenemedi');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAgents();
    }, [fetchAgents]);

    const handleAdd = () => {
        form.resetFields();
        setModalVisible(true);
    };

    const handleDelete = (agent) => {
        setDeleteTarget(agent);
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            await adminService.deleteAgent(deleteTarget.id);
            message.success('Temsilci silindi');
            setDeleteTarget(null);
            fetchAgents();
        } catch (err) {
            message.error(err.response?.data?.message || 'Silme işlemi başarısız');
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            await adminService.createAgent(values);
            message.success('Temsilci oluşturuldu');
            setModalVisible(false);
            fetchAgents();
        } catch (err) {
            if (err.errorFields) return;
            message.error(err.response?.data?.message || 'İşlem hatası');
        }
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 80,
        },
        {
            title: 'Görünen Ad',
            dataIndex: 'displayName',
            key: 'displayName',
            render: (text) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div className="admin-avatar">
                        <UserOutlined />
                    </div>
                    <span style={{ fontWeight: 500, color: 'var(--admin-text-primary)' }}>{text}</span>
                </div>
            ),
        },
        {
            title: 'E-posta',
            dataIndex: 'email',
            key: 'email',
            render: (text) => (
                <span style={{ color: 'var(--admin-text-secondary)' }}>
                    {text || '-'}
                </span>
            ),
        },
        {
            title: 'Rol',
            dataIndex: 'role',
            key: 'role',
            width: 150,
            render: (role) => {
                const meta = {
                    ADMIN: { label: 'Yönetici', className: 'admin-tag-success' },
                    CONTENT_ADMIN: { label: 'İçerik Yöneticisi', className: 'admin-tag-primary' },
                    CALL_CENTER: { label: 'Çağrı Merkezi', className: 'admin-tag-warning' },
                }[role] || { label: role, className: '' };
                return <Tag className={`admin-tag ${meta.className}`}>{meta.label}</Tag>;
            },
        },
        {
            title: '',
            key: 'actions',
            fixed: 'right',
            width: 60,
            render: (_, record) => (
                <Tooltip title="Temsilciyi Sil">
                    <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(record)}
                    />
                </Tooltip>
            ),
        },
    ];

    return (
        <div>
            <div className="admin-page-header">
                <div>
                    <h2 style={{ margin: 0, color: 'var(--admin-text-primary)' }}>Temsilciler</h2>
                    <p style={{ margin: '4px 0 0', color: 'var(--admin-text-secondary)', fontSize: 14 }}>
                        Admin panel temsilcilerini yönetin
                    </p>
                </div>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button
                        className="admin-btn-primary"
                        icon={<PlusOutlined />}
                        onClick={handleAdd}
                    >
                        Yeni Temsilci
                    </Button>
                </motion.div>
            </div>

            <motion.div
                className="admin-table-container"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
            >
                {loading ? (
                    <div className="admin-loading">
                        <Spin size="large" />
                    </div>
                ) : (
                    <Table
                        scroll={{ x: 'max-content' }}
                        columns={columns}
                        dataSource={agents}
                        rowKey="id"
                        pagination={false}
                    />
                )}
            </motion.div>

            {/* Yeni Temsilci Modal */}
            <AnimatePresence>
                {modalVisible && (
                    <Modal
                        title={
                            <div className="admin-modal-heading">
                                <div className="admin-modal-heading-icon">
                                    <UserAddOutlined />
                                </div>
                                <div>
                                    <div className="admin-modal-heading-title">Yeni Temsilci Ekle</div>
                                    <div className="admin-modal-heading-subtitle">
                                        Yönetim paneline erişecek yeni bir kullanıcı oluşturun
                                    </div>
                                </div>
                            </div>
                        }
                        open={modalVisible}
                        onOk={handleSubmit}
                        onCancel={() => setModalVisible(false)}
                        okText="Oluştur"
                        cancelText="İptal"
                        width="92vw"
                        style={{ maxWidth: 500 }}
                        className="admin-modal"
                        rootClassName="admin-modal"
                        okButtonProps={{ className: 'admin-btn-primary' }}
                        cancelButtonProps={{ className: 'admin-btn-secondary' }}
                    >
                        <Form form={form} layout="vertical" style={{ marginTop: 20 }}>
                            <Form.Item
                                name="userName"
                                label="Kullanıcı Adı"
                                rules={[
                                    { required: true, message: 'Kullanıcı adı gerekli' },
                                    { min: 3, message: 'En az 3 karakter olmalı' },
                                ]}
                            >
                                <Input placeholder="ornek_kullanici" />
                            </Form.Item>
                            <Form.Item
                                name="displayName"
                                label="Görünen Ad"
                                rules={[{ required: true, message: 'Görünen ad gerekli' }]}
                            >
                                <Input placeholder="Ahmet Yılmaz" />
                            </Form.Item>
                            <Form.Item
                                name="email"
                                label="E-posta"
                                rules={[
                                    { required: true, message: 'E-posta gerekli' },
                                    { type: 'email', message: 'Geçerli bir e-posta girin' },
                                ]}
                            >
                                <Input placeholder="ahmet@lidya.com" />
                            </Form.Item>
                            <Form.Item
                                name="password"
                                label="Şifre"
                                rules={[
                                    { required: true, message: 'Şifre gerekli' },
                                    { min: 8, message: 'En az 8 karakter olmalı' },
                                ]}
                            >
                                <Input.Password placeholder="********" />
                            </Form.Item>
                            <Form.Item
                                name="role"
                                label="Rol"
                                initialValue="CONTENT_ADMIN"
                            >
                                <Select>
                                    <Select.Option value="CONTENT_ADMIN">İçerik Yöneticisi</Select.Option>
                                    <Select.Option value="CALL_CENTER">Çağrı Merkezi</Select.Option>
                                    <Select.Option value="ADMIN">Yönetici</Select.Option>
                                </Select>
                            </Form.Item>
                        </Form>
                    </Modal>
                )}
            </AnimatePresence>

            {/* Silme Onay Modal */}
            <Modal
                title={
                    <div className="admin-modal-heading">
                        <div className="admin-modal-heading-icon danger">
                            <ExclamationCircleOutlined />
                        </div>
                        <div>
                            <div className="admin-modal-heading-title">Temsilciyi Sil</div>
                            <div className="admin-modal-heading-subtitle">Bu işlem geri alınamaz</div>
                        </div>
                    </div>
                }
                open={!!deleteTarget}
                onOk={confirmDelete}
                onCancel={() => setDeleteTarget(null)}
                okText="Sil"
                cancelText="Vazgeç"
                width="92vw"
                style={{ maxWidth: 420 }}
                className="admin-modal"
                rootClassName="admin-modal"
                okButtonProps={{ danger: true, type: 'primary' }}
                cancelButtonProps={{ className: 'admin-btn-secondary' }}
            >
                <p style={{ color: 'var(--admin-text-secondary)', fontSize: 14, margin: '16px 0' }}>
                    <strong style={{ color: 'var(--admin-text-primary)' }}>"{deleteTarget?.displayName}"</strong> adlı
                    temsilciyi silmek istediğinize emin misiniz?
                </p>
            </Modal>
        </div>
    );
};

export default AgentsPage;
